// ============================================
// BOÎTES MAIL — DIAGNOSTIC LISIBLE
// ============================================
// Traduit l'état technique d'un connecteur (statut, dernière lecture, message
// d'erreur Microsoft) en trois phrases pour un administrateur non technicien :
// ce qui se passe, pourquoi, et quoi faire. Couvre aussi l'envoi par la boîte
// (boîte d'envoi, e-mail de test). Aucune dépendance à React : tout est testé
// à part.

import type { BilanSynchro, CodeErreurEnvoi, MailConnecteurDetaille, StatutEmailEntrant } from '../../services/api';
import type { ResultatTestConnecteur } from '../../types';
import { formatDateHeure } from '../../shared/dates';

export type TonEtat = 'ok' | 'neutre' | 'alerte' | 'erreur';

export type CodeEtat =
  | 'ACTIF'
  | 'SUSPENDU'
  | 'ERREUR'
  | 'JAMAIS_SYNCHRONISEE'
  | 'MUETTE'
  | 'INGESTION_COUPEE';

export interface Diagnostic {
  code: CodeEtat;
  libelle: string;
  ton: TonEtat;
  /** Ce qui se passe, en clair. */
  cause: string;
  /** Le geste à faire ; null quand il n'y a rien à faire. */
  action: string | null;
}

export interface ContexteDiagnostic {
  maintenant?: Date;
  /** Paramètre « Alerte si une boîte est muette depuis (minutes) ». */
  silenceMinutes?: number;
  /** Interrupteur général « Créer des dossiers depuis les boîtes mail ».
   *  `undefined` quand il n'a pas pu être lu : on ne conclut rien. */
  ingestionActive?: boolean;
}

/** Classes des pastilles d'état : reprises telles quelles de l'écran d'origine. */
export const CLASSE_TON: Record<TonEtat, string> = {
  ok: 'bg-green-100 text-green-800',
  neutre: 'bg-brume text-graphite',
  alerte: 'bg-amber-100 text-amber-800',
  erreur: 'bg-red-100 text-red-800',
};

interface Explication { cause: string; action: string }

/**
 * Motifs d'erreur Microsoft (Entra ID, Graph) reconnus, du plus précis au
 * plus général. Les codes AADSTS sont stables et documentés par Microsoft.
 */
const MOTIFS: Array<{ test: RegExp; cause: string; action: string }> = [
  {
    test: /AADSTS7000222|secret.{0,40}expir|expired.{0,40}secret/i,
    cause: 'Le secret client de l’application Microsoft a expiré.',
    action: 'Dans Entra ID, ouvrez l’application, « Certificats et secrets », créez un nouveau secret, puis collez sa valeur ici avec « Modifier ».',
  },
  {
    test: /AADSTS7000215|invalid_client|invalid client secret/i,
    cause: 'Le secret client saisi n’est pas reconnu par Microsoft.',
    action: 'Collez la « Valeur » du secret (et non son « ID ») avec « Modifier ». En cas de doute, créez un nouveau secret dans Entra ID.',
  },
  {
    test: /AADSTS700016|application.{0,40}not found|unauthorized_client/i,
    cause: 'L’identifiant de l’application est inconnu dans ce tenant.',
    action: 'Vérifiez l’« ID d’application (client) » et l’« ID de l’annuaire (locataire) » sur la page « Vue d’ensemble » de l’application dans Entra ID.',
  },
  {
    test: /AADSTS90002|AADSTS900023|tenant.{0,40}(not found|invalid)|invalid_tenant/i,
    cause: 'Le tenant (annuaire) Microsoft indiqué n’existe pas.',
    action: 'Recopiez l’« ID de l’annuaire (locataire) » depuis la page « Vue d’ensemble » de l’application dans Entra ID.',
  },
  {
    test: /AADSTS65001|consent/i,
    cause: 'Le consentement administrateur n’a pas été donné à l’application.',
    action: 'Dans Entra ID, « Autorisations de l’API », cliquez « Accorder un consentement d’administrateur » pour Mail.Read, Mail.ReadWrite et Mail.Send.',
  },
  {
    test: /ErrorAccessDenied|Access is denied|Authorization_RequestDenied|\b403\b|Forbidden/i,
    cause: 'L’application n’a pas le droit de lire cette boîte.',
    action: 'Vérifiez que les autorisations Mail.Read, Mail.ReadWrite et Mail.Send de type « Application » sont accordées avec consentement administrateur, et que la boîte fait partie de la stratégie d’accès (Application Access Policy) si elle existe.',
  },
  {
    test: /MailboxNotEnabledForRESTAPI|ResourceNotFound|ErrorInvalidUser|\b404\b|not found/i,
    cause: 'Microsoft ne trouve pas cette boîte aux lettres.',
    action: 'Vérifiez l’adresse saisie. La boîte doit exister dans Microsoft 365 (boîte partagée ou utilisateur avec licence Exchange).',
  },
  {
    test: /\b429\b|throttl|too many requests/i,
    cause: 'Microsoft limite temporairement le nombre d’appels.',
    action: 'Rien à faire : la lecture reprendra seule au prochain cycle.',
  },
  {
    test: /ENOTFOUND|ETIMEDOUT|ECONNRESET|ECONNREFUSED|fetch failed|network|délai dépassé|timeout/i,
    cause: 'Le serveur du SAV n’a pas réussi à joindre Microsoft.',
    action: 'Réessayez dans quelques minutes. Si l’erreur persiste, prévenez l’équipe technique (accès internet du serveur).',
  },
  {
    test: /subscription|abonnement|notificationUrl|webhook/i,
    cause: 'L’abonnement de notification en temps réel n’a pas pu être créé ou renouvelé.',
    action: 'Les e-mails restent lus par le cycle de rattrapage. Prévenez l’équipe technique : l’adresse publique du webhook doit être joignable par Microsoft.',
  },
  {
    test: /illisible|déchiffr|decrypt/i,
    cause: 'Le secret enregistré ne peut plus être relu par le serveur.',
    action: 'Ressaisissez le secret client avec « Modifier ».',
  },
];

/** Traduit un message d'erreur technique en cause et action lisibles. */
export function expliquerErreur(motif: string | null | undefined): Explication {
  const texte = (motif ?? '').trim();
  if (!texte) {
    return {
      cause: 'La dernière lecture a échoué sans motif détaillé.',
      action: 'Cliquez sur « Tester la connexion » pour obtenir le détail.',
    };
  }
  const trouve = MOTIFS.find(m => m.test.test(texte));
  if (trouve) return { cause: trouve.cause, action: trouve.action };
  return {
    cause: 'Microsoft a refusé la lecture de la boîte.',
    action: 'Cliquez sur « Tester la connexion ». Si l’erreur persiste, transmettez le détail technique à l’équipe technique.',
  };
}

/** « il y a 5 min », « il y a 3 h », « il y a 2 j ». */
export function depuis(iso: string | null | undefined, maintenant = new Date()): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const min = Math.max(0, Math.floor((maintenant.getTime() - t) / 60000));
  if (min < 1) return 'à l’instant';
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

/** Date et heure en français, à l'heure de Paris, ou un tiret. */
export function dateLisible(iso: string | null | undefined): string {
  return formatDateHeure(iso, '—');
}

export function diagnostiquer(
  c: Pick<MailConnecteurDetaille, 'status' | 'lastError' | 'lastSyncAt'>,
  ctx: ContexteDiagnostic = {},
): Diagnostic {
  const maintenant = ctx.maintenant ?? new Date();

  if (c.status === 'INACTIF') {
    return {
      code: 'SUSPENDU',
      libelle: 'Suspendue',
      ton: 'neutre',
      cause: 'La boîte n’est pas lue : les e-mails restent dans Outlook et ne créent aucun dossier.',
      action: 'Cliquez sur « Activer ». Seuls les e-mails reçus après l’activation deviendront des dossiers.',
    };
  }

  if (c.status === 'ERREUR') {
    const e = expliquerErreur(c.lastError);
    return { code: 'ERREUR', libelle: 'En erreur', ton: 'erreur', cause: e.cause, action: e.action };
  }

  // Connecteur en service.
  if (ctx.ingestionActive === false) {
    return {
      code: 'INGESTION_COUPEE',
      libelle: 'Activée, mais non lue',
      ton: 'alerte',
      cause: 'L’interrupteur général « Créer des dossiers depuis les boîtes mail » est coupé : aucune boîte n’est lue.',
      action: 'Ouvrez Paramètres, rubrique « Ingestion e-mail », et activez cet interrupteur.',
    };
  }

  if (!c.lastSyncAt) {
    return {
      code: 'JAMAIS_SYNCHRONISEE',
      libelle: 'Jamais synchronisée',
      ton: 'alerte',
      cause: 'La boîte est activée mais n’a encore jamais été lue.',
      action: 'Cliquez sur « Synchroniser maintenant », ou attendez le prochain cycle de lecture.',
    };
  }

  const seuil = ctx.silenceMinutes ?? 60;
  const minutes = Math.floor((maintenant.getTime() - new Date(c.lastSyncAt).getTime()) / 60000);
  if (Number.isFinite(minutes) && minutes >= seuil) {
    return {
      code: 'MUETTE',
      libelle: 'Muette',
      ton: 'alerte',
      cause: `Aucune lecture réussie ${depuis(c.lastSyncAt, maintenant)} (seuil d’alerte : ${seuil} min).`,
      action: 'Cliquez sur « Tester la connexion » puis « Synchroniser maintenant ». Si rien ne change, prévenez l’équipe technique.',
    };
  }

  return {
    code: 'ACTIF',
    libelle: 'Active',
    ton: 'ok',
    cause: `La boîte est lue normalement. Dernière lecture ${depuis(c.lastSyncAt, maintenant)}.`,
    action: null,
  };
}

/** Phrase de résultat d'un test de connexion. */
export function resumerTest(r: ResultatTestConnecteur): { ok: boolean; message: string; detail?: string } {
  if (r.ok) {
    const n = r.messagesVus ?? 0;
    return {
      ok: true,
      message: `Connexion réussie : la boîte est accessible (${n} message${n > 1 ? 's' : ''} visible${n > 1 ? 's' : ''} dans la réception).`,
    };
  }
  const e = expliquerErreur(r.motif);
  return { ok: false, message: `Connexion impossible. ${e.cause} ${e.action}`, detail: r.motif };
}

function pluriel(n: number, un: string, plusieurs: string): string {
  return `${n} ${n > 1 ? plusieurs : un}`;
}

/** Phrase de résultat d'une synchronisation manuelle. */
export function resumerSynchro(r: { bilan: BilanSynchro | null; motif?: string; avertissement?: string }): { ok: boolean; message: string } {
  if (!r.bilan) {
    return { ok: false, message: `Synchronisation non lancée : ${r.motif ?? 'motif inconnu'}` };
  }
  const b = r.bilan;
  if (b.lus === 0 && !r.avertissement) {
    return { ok: true, message: 'Synchronisation terminée : aucun nouvel e-mail.' };
  }
  const morceaux = [
    pluriel(b.lus, 'e-mail lu', 'e-mails lus'),
    pluriel(b.crees, 'dossier créé', 'dossiers créés'),
    pluriel(b.rattaches, 'ajouté à un dossier existant', 'ajoutés à des dossiers existants'),
    pluriel(b.ignores, 'ignoré', 'ignorés'),
  ];
  if (b.erreurs > 0) morceaux.push(pluriel(b.erreurs, 'en erreur', 'en erreur'));
  const base = `Synchronisation terminée : ${morceaux.join(', ')}.`;
  return {
    ok: b.erreurs === 0 && !r.avertissement,
    message: r.avertissement ? `${base} Attention : ${r.avertissement}` : base,
  };
}

/** Libellé et ton de chaque statut d'e-mail reçu. */
export const STATUT_EMAIL: Record<StatutEmailEntrant, { libelle: string; ton: TonEtat }> = {
  RECU: { libelle: 'En cours de traitement', ton: 'neutre' },
  DOSSIER_CREE: { libelle: 'Dossier créé', ton: 'ok' },
  RATTACHE: { libelle: 'Ajouté à un dossier', ton: 'ok' },
  A_QUALIFIER: { libelle: 'À qualifier', ton: 'alerte' },
  IGNORE_AUTO_REPLY: { libelle: 'Ignoré : réponse automatique', ton: 'neutre' },
  IGNORE_SPAM: { libelle: 'Ignoré : indésirable', ton: 'neutre' },
  IGNORE_INTERNE: { libelle: 'Ignoré : expéditeur interne', ton: 'neutre' },
  IGNORE_NON_REMIS: { libelle: 'Ignoré : avis de non-remise', ton: 'neutre' },
  IGNORE_NOTIFICATION: { libelle: 'Écarté : notification automatique', ton: 'neutre' },
  ECARTE_IA: { libelle: 'Écarté : analyse IA', ton: 'neutre' },
  ERREUR: { libelle: 'En erreur', ton: 'erreur' },
};

export function statutEmail(s: string): { libelle: string; ton: TonEtat } {
  return STATUT_EMAIL[s as StatutEmailEntrant] ?? { libelle: s, ton: 'neutre' };
}

/** Texte de la confirmation avant d'activer ou de suspendre une boîte. */
export function texteConfirmationBascule(
  c: Pick<MailConnecteurDetaille, 'status' | 'label' | 'mailbox'> & Partial<Pick<MailConnecteurDetaille, 'envoiActif'>>,
): { titre: string; effet: string; bouton: string } {
  if (c.status === 'ACTIF' || c.status === 'ERREUR') {
    // Le serveur retire le rôle d'envoi à une boîte suspendue : elle n'est
    // plus lue, les réponses des clients à ses envois seraient perdues.
    const envoi = estBoiteEnvoi(c)
      ? ' C’est la boîte d’envoi : elle cessera aussi d’envoyer les e-mails du SAV, qui passeront par le serveur SMTP s’il est configuré, sinon ne partiront pas. Après réactivation, il faudra la choisir de nouveau comme boîte d’envoi.'
      : '';
    return {
      titre: `Suspendre « ${c.label} » ?`,
      effet: `La boîte ${c.mailbox} ne sera plus lue. Les e-mails reçus pendant la suspension resteront dans Outlook et ne deviendront pas des dossiers, même après réactivation : ils seront à traiter à la main.${envoi}`,
      bouton: 'Suspendre',
    };
  }
  return {
    titre: `Activer « ${c.label} » ?`,
    effet: `La boîte ${c.mailbox} sera lue automatiquement. Seuls les e-mails reçus à partir de maintenant deviendront des dossiers : l’historique de la boîte n’est pas importé.`,
    bouton: 'Activer',
  };
}

// --- Envoi depuis la boîte (Microsoft Graph, Mail.Send) ------------------

/** Boîte d'envoi : absent (ancien serveur) vaut false. */
export function estBoiteEnvoi(c: Partial<Pick<MailConnecteurDetaille, 'envoiActif'>>): boolean {
  return c.envoiActif === true;
}

/** Seule une boîte lue (ACTIF ou ERREUR) peut devenir la boîte d'envoi : les
 *  clients répondent à l'adresse d'envoi, et une boîte suspendue ne lirait
 *  pas leurs réponses (le serveur refuse aussi). */
export function peutDevenirBoiteEnvoi(c: Pick<MailConnecteurDetaille, 'status' | 'provider'>): boolean {
  return c.provider === 'MICROSOFT' && (c.status === 'ACTIF' || c.status === 'ERREUR');
}

/** Réglages qui envoient des e-mails aux clients : ils partent dès qu'une
 *  boîte d'envoi est choisie, s'ils sont déjà actifs. */
export const REGLAGES_ENVOI: readonly string[] = [
  'sav.accuse_reception.actif',
  'sav.notif_statut.actif',
  'sav.notif_message.actif',
  'sav.relances.actif',
];

/** Libellés des réglages d'envoi déjà actifs, dans l'ordre de `REGLAGES_ENVOI`. */
export function reglagesEnvoiActifs(parametres: ReadonlyArray<{ cle: string; libelle: string; valeur: string | null }>): string[] {
  return REGLAGES_ENVOI
    .map(cle => parametres.find(p => p.cle === cle))
    .filter((p): p is { cle: string; libelle: string; valeur: string | null } => p?.valeur === 'true')
    .map(p => p.libelle);
}

/**
 * Texte de la confirmation avant de faire d'une boîte la boîte d'envoi, ou de
 * la lui retirer. `actuelle` : la boîte d'envoi en place, s'il y en a une autre.
 * `reglagesActifs` : libellés des réglages d'envoi déjà actifs (voir
 * `reglagesEnvoiActifs`) ; non lus (undefined), rien n'en est dit.
 */
export function texteConfirmationEnvoi(
  c: Pick<MailConnecteurDetaille, 'label' | 'mailbox' | 'envoiActif'>,
  actuelle?: Pick<MailConnecteurDetaille, 'label' | 'mailbox'> | null,
  reglagesActifs?: readonly string[],
): { titre: string; effet: string; bouton: string } {
  if (estBoiteEnvoi(c)) {
    return {
      titre: `Ne plus envoyer depuis « ${c.label} » ?`,
      effet: `Les e-mails du SAV ne partiront plus de ${c.mailbox}. Ils passeront par le serveur SMTP s’il est configuré ; sinon, ils ne seront pas envoyés. La lecture de la boîte n’est pas modifiée.`,
      bouton: 'Ne plus envoyer',
    };
  }
  const remplacement = actuelle && actuelle.mailbox !== c.mailbox
    ? ` ${actuelle.mailbox} cessera d’être la boîte d’envoi : une seule boîte peut l’être.`
    : '';
  const reglages = reglagesActifs === undefined
    ? ''
    : reglagesActifs.length > 0
      ? ` Réglages déjà actifs, qui enverront des e-mails réels aux clients dès maintenant : ${reglagesActifs.map(l => `« ${l} »`).join(', ')}.`
      : ' Aucun envoi automatique aux clients n’est activé pour l’instant (Paramètres, rubrique Dossiers SAV).';
  return {
    titre: `Envoyer depuis « ${c.label} » ?`,
    effet: `Les e-mails du SAV (accusés de réception, réponses aux clients, changements de statut, relances) partiront de ${c.mailbox} par Microsoft 365, et non plus par le serveur SMTP. Une réponse à un client reste dans sa conversation, et une copie est gardée dans les Éléments envoyés.${remplacement}${reglages} L’application Microsoft doit avoir l’autorisation Mail.Send : envoyez un e-mail de test juste après.`,
    bouton: 'Envoyer depuis cette boîte',
  };
}

/** Phrase affichée sur la carte après la bascule. */
export function messageBasculeEnvoi(c: Pick<MailConnecteurDetaille, 'mailbox'>, envoiActif: boolean): string {
  return envoiActif
    ? `Boîte d’envoi : les e-mails du SAV partent désormais de ${c.mailbox}. Envoyez un e-mail de test pour vérifier.`
    : 'Cette boîte n’envoie plus les e-mails du SAV.';
}

const CODES_ENVOI: ReadonlySet<string> = new Set<CodeErreurEnvoi>([
  'ENVOI_REFUSE_PERMISSION', 'ENVOI_BOITE_INTROUVABLE', 'ENVOI_ECHEC',
]);

/**
 * Traduit l'échec d'un e-mail de test. `code` vient de l'API (CodeErreurEnvoi) ;
 * tout autre code (droits SAV, adresse refusée, réseau) garde le message du serveur.
 */
export function expliquerErreurEnvoi(
  code: string | null | undefined,
  message: string | null | undefined,
  mailbox: string,
): { cause: string; action: string; detail?: string } {
  const detail = (message ?? '').trim() || undefined;
  switch (code && CODES_ENVOI.has(code) ? (code as CodeErreurEnvoi) : null) {
    case 'ENVOI_REFUSE_PERMISSION':
      return {
        cause: `Microsoft refuse l’envoi depuis ${mailbox}.`,
        action: 'Dans Entra ID, « Autorisations de l’API », ajoutez Mail.Send (type « Application ») puis cliquez « Accorder un consentement d’administrateur ». Si une stratégie d’accès (New-ApplicationAccessPolicy) existe, vérifiez que cette boîte fait partie du groupe autorisé : la stratégie limite aussi l’envoi.',
        detail,
      };
    case 'ENVOI_BOITE_INTROUVABLE':
      return {
        cause: `Microsoft ne trouve pas la boîte ${mailbox}.`,
        action: 'Vérifiez l’adresse avec « Modifier ». La boîte doit exister dans Microsoft 365 (boîte partagée ou utilisateur avec licence Exchange).',
        detail,
      };
    case 'ENVOI_ECHEC':
      return {
        cause: 'Microsoft n’a pas accepté l’envoi.',
        action: 'Réessayez dans quelques minutes. Si l’erreur persiste, transmettez le détail technique à l’équipe technique.',
        detail,
      };
    default:
      return {
        cause: detail ? `Envoi impossible : ${detail}` : 'Envoi impossible.',
        action: 'Vérifiez l’adresse du destinataire et réessayez.',
      };
  }
}

/** Phrase de résultat d'un e-mail de test réussi. */
export function resumerTestEnvoi(r: { to: string }, mailbox: string): string {
  return `E-mail de test envoyé à ${r.to} depuis ${mailbox}. Vérifiez sa réception, y compris dans le courrier indésirable.`;
}

/** Adresse e-mail plausible : sert seulement à griser le bouton d'envoi. */
export function adresseValide(a: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.trim());
}
